import { createContext, useContext } from 'react';

// Fuera de core/chatbot, como useIsAIEnabled, porque lo leen vistas que no son el chat (la página
// de resultados, la práctica) y no deben depender de sus contextos.

export const AI_QUOTA_USAGE_STATUS = {
  UNUSED: 'unused',
  IN_USE: 'in-use',
  EXHAUSTED: 'exhausted',
} as const;

export type AIQuotaUsageStatus = (typeof AI_QUOTA_USAGE_STATUS)[keyof typeof AI_QUOTA_USAGE_STATUS];

/** Lo que devuelve el back: `quota: null` es una cuota sin límite. */
export type AIQuotaSnapshot = {
  consumed: number;
  quota: number | null;
};

export type AIQuotaUsage = {
  /** Tal cual llega del back: puede pasar de `quota` si dos turnos gastan a la vez. */
  consumed: number;
  percent: number;
  quota: number;
  status: AIQuotaUsageStatus;
};

export type AIQuotaValue = {
  /** Vuelve a leer la cuota. Nunca rechaza: si falla, se queda el último dato bueno. */
  refreshQuota: () => Promise<void>;
  /** El back rechazó un turno por falta de cupo: queda agotada al momento, aunque la relectura falle. */
  reportQuotaExhausted: () => void;
  /** Cuota con la que cerró un turno, o null si el cierre no la trajo: entonces se vuelve a leer, si ya había una. */
  reportTurnQuota: (snapshot: AIQuotaSnapshot | null) => void;
  /** null sin cuota, con cuota sin límite o mientras no se ha podido leer. */
  usage: AIQuotaUsage | null;
};

const NO_QUOTA: AIQuotaValue = {
  refreshQuota: () => Promise.resolve(),
  reportQuotaExhausted: () => {},
  reportTurnQuota: () => {},
  usage: null,
};

export const AIQuotaContext = createContext<AIQuotaValue>(NO_QUOTA);

export function useAIQuota(): AIQuotaValue {
  return useContext(AIQuotaContext);
}
